import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { CampaignFilterType } from "../../../campaign/list/types";
import { UserFilters, UsersSortOptions } from "../../../user/types";
import { CompanyFilters } from "../../../company";
import { changeActiveSite } from "../site/site-slice";
import { SortOptions } from "../../../ui/layout/listPages";

type Extendable = {
  isExtendedCard: boolean;
};

type Sortable<T> = {
  sortBy: keyof T;
};

type CampaignsUIState = Extendable & {
  showOnlyActive: boolean;
  selectedFilters: CampaignFilterType[];
};

type UsersUIState = Extendable &
  Sortable<UsersSortOptions> & {
    selectedFilters: UserFilters[];
  };
type SitesUIState = Extendable;
type BoostingUIState = Extendable;
type CompaniesUIState = {
  selectedFilters: CompanyFilters[];
};

type UIState = {
  campaigns: CampaignsUIState;
  users: UsersUIState;
  sites: SitesUIState;
  boosting: BoostingUIState;
  companies: CompaniesUIState;
};

type ExtendedCardPayload = {
  feature: keyof Omit<UIState, "companies">;
  value: boolean;
};

type ShowOnlyActivePayload = {
  feature: keyof Pick<UIState, "campaigns">;
  value: boolean;
};

type SortByPayload = {
  feature: keyof Pick<UIState, "users">;
  // ListSettings.handleSort is typed by the shared SortOptions union ("name"|"email"|"date"),
  // not per-consumer — "users" only ever actually dispatches "name"/"email" (its own
  // sortOptions object has no "date" key), so the reducer narrows on write below.
  value: SortOptions;
};

type SelectedFiltersPayload = {
  feature: keyof Pick<UIState, "campaigns" | "companies" | "users">;
  value:
    | CampaignsUIState["selectedFilters"]
    | CompaniesUIState["selectedFilters"]
    | UsersUIState["selectedFilters"];
};

const initialState: UIState = {
  campaigns: {
    showOnlyActive: false,
    isExtendedCard: false,
    selectedFilters: [],
  },
  users: {
    isExtendedCard: false,
    sortBy: "name",
    selectedFilters: [],
  },
  sites: {
    isExtendedCard: false,
  },
  boosting: {
    isExtendedCard: false,
  },
  companies: {
    selectedFilters: [],
  },
};

const UISlice = createSlice({
  name: "ui-state",
  initialState,

  reducers: {
    setIsExtendedCard(
      state,
      { payload: { feature, value } }: PayloadAction<ExtendedCardPayload>,
    ) {
      state[feature].isExtendedCard = value;
    },
    setShowOnlyActive(
      state,
      { payload: { feature, value } }: PayloadAction<ShowOnlyActivePayload>,
    ) {
      state[feature].showOnlyActive = value;
    },
    setSortBy(
      state,
      { payload: { feature, value } }: PayloadAction<SortByPayload>,
    ) {
      state[feature].sortBy = value as keyof UsersSortOptions;
    },
    setSelectedFilters(
      state,
      { payload: { feature, value } }: PayloadAction<SelectedFiltersPayload>,
    ) {
      state[feature].selectedFilters = value;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(changeActiveSite, (state) => {
      state.campaigns.selectedFilters = [];
      state.campaigns.showOnlyActive = false;
    });
  },
});

export const {
  setIsExtendedCard,
  setShowOnlyActive,
  setSortBy,
  setSelectedFilters,
} = UISlice.actions;

export default UISlice.reducer;
